
import { Injectable, inject, Renderer2, DOCUMENT } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ThemeSettings {
    isDarkTheme: boolean;
    isDarkSidebar: boolean;
    selectedBgColor: string;
    isRtl: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class ThemeService {
    private document = inject(DOCUMENT);

    private themeSettingsSubject = new BehaviorSubject<ThemeSettings>({
        isDarkTheme: false,
        isDarkSidebar: false,
        selectedBgColor: 'white',
        isRtl: false,
    });

    themeSettings$ = this.themeSettingsSubject.asObservable();

    get currentTheme(): ThemeSettings {
        return this.themeSettingsSubject.value;
    }

    loadThemeSettings(): void {
        const settings: ThemeSettings = {
            isDarkTheme: localStorage.getItem('theme') === 'dark',
            isDarkSidebar: localStorage.getItem('menuOption') === 'menu_dark',
            selectedBgColor: localStorage.getItem('choose_skin_active') || 'white',
            isRtl: localStorage.getItem('isRtl') === 'true',
        };

        this.themeSettingsSubject.next(settings);
    }

    updateThemeSettings(updates: Partial<ThemeSettings>, renderer?: Renderer2): void {
        const current = this.currentTheme;
        const newSettings = { ...current, ...updates };

        if (renderer) {
            this.applyThemeWithRenderer(newSettings, renderer);
        }

        this.saveThemeSettings(newSettings);
        this.themeSettingsSubject.next(newSettings);
    }

    private applyThemeWithRenderer(settings: ThemeSettings, renderer: Renderer2): void {
        if (settings.isDarkTheme) {
            renderer.removeClass(this.document.body, 'light');
            renderer.addClass(this.document.body, 'dark');
            renderer.addClass(this.document.body, 'theme-black');
        }
        else {
            renderer.removeClass(this.document.body, 'dark');
            renderer.addClass(this.document.body, 'light');
            renderer.addClass(this.document.body, 'theme-white');
        }

        if (settings.isDarkSidebar) {
            renderer.removeClass(this.document.body, 'menu_light');
            renderer.addClass(this.document.body, 'menu_dark');
        } 
        else {
            renderer.removeClass(this.document.body, 'menu_dark');
            renderer.addClass(this.document.body, 'menu_light');
        }

        renderer.addClass(this.document.body, `theme-${settings.selectedBgColor}`);

        if (settings.isRtl) {
            this.document.getElementsByTagName('html')[0].setAttribute('dir', 'rtl');
            renderer.addClass(this.document.body, 'rtl');
        } 
        else {
            this.document.getElementsByTagName('html')[0].removeAttribute('dir');
            renderer.removeClass(this.document.body, 'rtl');
        }
    }

    private saveThemeSettings(settings: ThemeSettings): void {
        localStorage.setItem('theme', settings.isDarkTheme ? 'dark' : 'light');
        localStorage.setItem('menuOption', settings.isDarkSidebar ? 'menu_dark' : 'menu_light');
        localStorage.setItem('choose_skin_active', settings.selectedBgColor);
        localStorage.setItem('choose_skin', `theme-${settings.selectedBgColor}`);
        localStorage.setItem('isRtl', settings.isRtl.toString());
    }
}
